import type React from 'react';
import { Banknote, CalendarClock, Landmark, Repeat } from 'lucide-react';
import type { BillingSkuRow } from '../../services/billingApi';

/**
 * WHAT A KIT COSTS, GIVEN THAT ITS PARTS ARE SOLD DIFFERENTLY.
 *
 * Selling terms hang off a DEVICE, never off a bundle, so a kit has no price
 * of its own — it has the prices of everything in it. That is fine while every
 * part can be sold the same way, and wrong the moment one cannot: a cash
 * drawer offered only as a rental cannot be bought outright, so quoting an
 * "outright kit price" that silently drops it, or that pretends its monthly
 * rent is a one-time charge, states a number nobody can be charged.
 *
 * So a quote has TWO buckets. The upfront money and the monthly money are
 * different kinds of money and are never added together — a kit is
 * "$1,050.00 upfront + $14.99/mo", and the parts that forced the second half
 * are named, because that is the fact an operator has to know before quoting.
 */

export const SALE_MODES = ['outright', 'installment', 'lease', 'rental'] as const;
export type SaleMode = (typeof SALE_MODES)[number];

/**
 * When a part cannot be sold on the mode being quoted, this is the order it
 * falls back through. Rental first: it is the lightest commitment and the one
 * a part is most often restricted to. `included` is handled separately — it
 * costs nothing on any mode.
 */
const FALLBACK_ORDER: SaleMode[] = ['rental', 'installment', 'lease', 'outright'];

export interface FinancePart {
  modelId: string;
  name: string;
  quantity: number;
  /** Every device_finance SKU pointing at this model. */
  offers: BillingSkuRow[];
}

/** One device's line in a quote — what it is, and what it costs on this mode. */
export interface QuoteLine {
  modelId: string;
  name: string;
  quantity: number;
  /** The mode this part was actually priced on — differs from the quote's own
   *  mode when the part could not be sold that way. */
  usedMode: SaleMode | 'included' | 'none';
  /** Per-unit price, as offered. Zero for `included` and `none`. */
  unitCents: number;
  /** `unitCents × quantity`, in whichever bucket `recurring` says. */
  lineCents: number;
  recurring: boolean;
  /** Term, for an installment or a fixed-term lease. */
  termMonths?: number;
  /**
   * The offer this line was priced from. This is what a DEAL actually holds —
   * a deal carries device SKUs, so adding a kit to one means adding exactly
   * these. Absent for `included` and `none`, which have nothing to add.
   */
  skuId?: string;
}

export interface ModeQuote {
  mode: SaleMode;
  /** One-time money: outright prices, and any down payment already priced in. */
  upfrontCents: number;
  /** Recurring money: rentals, installments and leases, per month. */
  monthlyCents: number;
  /** Parts that could NOT be sold on `mode` and what they were priced as. */
  fallbacks: { name: string; usedMode: SaleMode }[];
  /** Parts carrying no terms at all — they are in no bucket. */
  missing: string[];
  /** How many parts this quote actually prices. */
  covered: number;
  /** Every part, in kit order — what it is charged as under this mode. */
  lines: QuoteLine[];
}

const cheapest = (offers: BillingSkuRow[], mode: SaleMode): BillingSkuRow | undefined => {
  const forMode = offers.filter((s) => s.saleMode === mode);
  if (!forMode.length) return undefined;
  return forMode.reduce((a, b) => (b.amountCents < a.amountCents ? b : a));
};

/**
 * One quote per mode any part can be sold on. A mode nothing can be sold on is
 * absent rather than shown as zero.
 */
export function quoteKit(parts: FinancePart[]): ModeQuote[] {
  const quotes: ModeQuote[] = [];

  for (const mode of SALE_MODES) {
    // Quote a mode only if SOMETHING in the kit is actually offered on it —
    // otherwise every kit would show four identical fallback quotes.
    if (!parts.some((p) => p.offers.some((s) => s.saleMode === mode))) continue;

    let upfrontCents = 0;
    let monthlyCents = 0;
    let covered = 0;
    const fallbacks: { name: string; usedMode: SaleMode }[] = [];
    const missing: string[] = [];
    const lines: QuoteLine[] = [];
    const base = (p: FinancePart) => ({ modelId: p.modelId, name: p.name, quantity: p.quantity });

    for (const p of parts) {
      if (!p.offers.length) {
        missing.push(p.name);
        lines.push({ ...base(p), usedMode: 'none', unitCents: 0, lineCents: 0, recurring: false });
        continue;
      }

      const direct = cheapest(p.offers, mode);
      if (direct) {
        const recurring = mode !== 'outright';
        const lineCents = direct.amountCents * p.quantity;
        if (recurring) monthlyCents += lineCents; else upfrontCents += lineCents;
        lines.push({
          ...base(p), usedMode: mode, unitCents: direct.amountCents, lineCents, recurring,
          termMonths: direct.termMonths, skuId: direct.skuId,
        });
        covered += 1;
        continue;
      }

      // Free with the kit costs nothing on any mode, and is not a fallback
      // worth naming — it is simply covered.
      if (p.offers.some((s) => s.saleMode === 'included')) {
        lines.push({ ...base(p), usedMode: 'included', unitCents: 0, lineCents: 0, recurring: false });
        covered += 1;
        continue;
      }

      const alt = FALLBACK_ORDER.map((m) => ({ m, sku: cheapest(p.offers, m) })).find((x) => x.sku);
      if (!alt?.sku) {
        missing.push(p.name);
        lines.push({ ...base(p), usedMode: 'none', unitCents: 0, lineCents: 0, recurring: false });
        continue;
      }

      const recurring = alt.m !== 'outright';
      const lineCents = alt.sku.amountCents * p.quantity;
      if (recurring) monthlyCents += lineCents; else upfrontCents += lineCents;
      lines.push({
        ...base(p), usedMode: alt.m, unitCents: alt.sku.amountCents, lineCents, recurring,
        termMonths: alt.sku.termMonths, skuId: alt.sku.skuId,
      });
      fallbacks.push({ name: p.name, usedMode: alt.m });
      covered += 1;
    }

    if (covered > 0) quotes.push({ mode, upfrontCents, monthlyCents, fallbacks, missing, covered, lines });
  }

  return quotes;
}

/**
 * HOW EACH WAY OF SELLING LOOKS. One icon and one hue per mode, defined once
 * so the Bundles list and the builder cannot describe the same offer in two
 * different colours. Whole class strings — Tailwind's scanner cannot see a
 * class built by interpolation.
 */
export const MODE_ICON: Record<SaleMode, React.FC<any>> = {
  outright: Banknote,
  installment: CalendarClock,
  lease: Landmark,
  rental: Repeat,
};

export const MODE_STYLE: Record<SaleMode, {
  /** The icon tile. */ badge: string;
  /** The mode's name. */ label: string;
  /** Resting state of a clickable quote tile. */ idle: string;
  /** Open state — the tile the breakdown below belongs to. */ open: string;
}> = {
  outright: {
    badge: 'bg-sky-100 text-sky-700',
    label: 'text-sky-700',
    idle: 'border-sky-100 bg-white hover:border-sky-300 hover:bg-sky-50/60',
    open: 'border-sky-400 bg-sky-50 ring-1 ring-sky-200',
  },
  installment: {
    badge: 'bg-rose-100 text-rose-700',
    label: 'text-rose-700',
    idle: 'border-rose-100 bg-white hover:border-rose-300 hover:bg-rose-50/60',
    open: 'border-rose-400 bg-rose-50 ring-1 ring-rose-200',
  },
  lease: {
    badge: 'bg-amber-100 text-amber-700',
    label: 'text-amber-700',
    idle: 'border-amber-100 bg-white hover:border-amber-300 hover:bg-amber-50/60',
    open: 'border-amber-400 bg-amber-50 ring-1 ring-amber-200',
  },
  rental: {
    badge: 'bg-emerald-100 text-emerald-700',
    label: 'text-emerald-700',
    idle: 'border-emerald-100 bg-white hover:border-emerald-300 hover:bg-emerald-50/60',
    open: 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200',
  },
};

/** "$1,050.00 upfront + $14.99/mo" — never one figure standing for both. */
export function quoteLabel(q: ModeQuote, money: (cents: number) => string): string {
  const parts: string[] = [];
  if (q.upfrontCents > 0 || q.monthlyCents === 0) parts.push(`${money(q.upfrontCents)} upfront`);
  if (q.monthlyCents > 0) parts.push(`${money(q.monthlyCents)}/mo`);
  return parts.join(' + ');
}

/** "Cash Drawer is rental-only" / "2 parts are rental-only". */
export function fallbackNote(q: ModeQuote): string | null {
  if (!q.fallbacks.length) return null;
  const byMode = new Map<SaleMode, string[]>();
  for (const f of q.fallbacks) byMode.set(f.usedMode, [...(byMode.get(f.usedMode) || []), f.name]);
  return [...byMode.entries()]
    .map(([m, names]) => `${names.join(', ')} ${names.length === 1 ? 'is' : 'are'} ${m}-only`)
    .join('; ');
}
